import { useEffect, useMemo, useState } from 'react'
import Modal from './Modal'
import { ApiError, SpendFigure, api } from '../lib/api'
import { compare, ownSpending } from '../lib/spending'
import { Plan } from '../types'

/**
 * How someone's spending at each shop compares with everyone else's (SM-19).
 *
 * The averages are the latest month the server has saved, each from at least
 * 25 people. The person's own figures come from their own plan — what their
 * imported statements showed for that month — and the comparison is worked
 * out here; nothing of theirs is sent for it. Only people who share their
 * spending see their own comparison; everyone sees the averages.
 */

type Averages = Awaited<ReturnType<typeof api.spendingAverages>>

type Load = { kind: 'loading' } | { kind: 'error'; message: string } | ({ kind: 'ready' } & Averages)

const pounds = (pence: number) => `£${(pence / 100).toFixed(2)}`

function monthName(month: string): string {
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

function differenceText(difference: number): string {
  const percent = Math.round(Math.abs(difference) * 100)
  if (percent < 3) return 'About the same'
  return `${percent}% ${difference > 0 ? 'more' : 'less'}`
}

const BAND_TEXT = { below: 'below most people', typical: 'like most people', above: 'above most people' } as const

export default function CompareSpendingModal({ plan, onClose }: { plan: Plan; onClose: () => void }) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' })

  useEffect(() => {
    let live = true
    api.spendingAverages().then(
      (averages) => live && setLoad({ kind: 'ready', ...averages }),
      (e) => live && setLoad({ kind: 'error', message: e instanceof ApiError ? e.message : 'Couldn’t load the averages.' }),
    )
    return () => {
      live = false
    }
  }, [])

  const own = useMemo(
    () => (load.kind === 'ready' && load.month ? ownSpending(plan, load.month) : new Map<string, { visits: number; amount: number }>()),
    [load, plan],
  )

  return (
    <Modal title="Compare my spending" onClose={onClose}>
      {load.kind === 'loading' && <p className="text-sm text-ink/70">Loading…</p>}
      {load.kind === 'error' && (
        <p role="alert" className="text-sm text-rag-bad">
          {load.message}
        </p>
      )}
      {load.kind === 'ready' && !load.month && (
        <p className="text-sm text-ink/80">
          No averages yet. A shop gets one once {load.minPeople} or more people share their spending there for a month,
          and a month is worked out after the month following it has ended.
        </p>
      )}
      {load.kind === 'ready' && load.month && (
        <Figures averages={load} month={load.month} own={own} />
      )}
      <div className="flex justify-end pt-3 border-t border-line mt-4">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-sm border border-line text-ink/80 hover:bg-paper">
          Close
        </button>
      </div>
    </Modal>
  )
}

function Figures({
  averages,
  month,
  own,
}: {
  averages: Averages
  month: string
  own: Map<string, { visits: number; amount: number }>
}) {
  const { sharing } = averages
  // Shops they spent at first, so their comparison is what they see first.
  const shops = [...averages.shops].sort(
    (a, b) => Number(own.has(b.subject)) - Number(own.has(a.subject)) || b.people - a.people,
  )
  const anyOwn = sharing && shops.some((s) => own.has(s.subject))

  return (
    <>
      <p className="text-sm text-ink/80 mb-3">
        What people spent in {monthName(month)}, per person. Each average leaves out the top and bottom 5% and comes from{' '}
        {averages.minPeople} or more people.
      </p>

      {!sharing && (
        <p className="text-sm text-ink bg-paper border border-line rounded-sm px-3 py-2 mb-3">
          To see how your own spending compares, tick <em>Also share how much I spend at these shops each month</em> when
          you next import a statement. Only people who share see their own comparison.
        </p>
      )}
      {sharing && !anyOwn && (
        <p className="text-sm text-ink/80 mb-3">
          None of the statements you've imported cover {monthName(month)} at these shops, so there's nothing of yours to
          compare yet.
        </p>
      )}

      <h3 className="text-sm text-ink mb-1">Shops</h3>
      <div className="overflow-x-auto mb-4">
        <table className="w-full text-sm">
          <thead className="text-xs text-ink/70 border-b border-line">
            <tr>
              <th className="text-left font-normal py-2 pr-3">Shop</th>
              <th className="text-right font-normal py-2 px-2">Average</th>
              <th className="text-right font-normal py-2 px-2">Most people</th>
              {sharing && <th className="text-right font-normal py-2 px-2">You</th>}
              {sharing && <th className="text-left font-normal py-2 pl-2">Compared</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {shops.map((s) => (
              <ShopRow key={s.subject} figure={s} own={sharing ? own.get(s.subject) : undefined} sharing={sharing} />
            ))}
          </tbody>
        </table>
      </div>

      {averages.categories.length > 0 && (
        <>
          <h3 className="text-sm text-ink mb-1">Kinds of spending</h3>
          <p className="text-xs text-ink/70 mb-1">Across the shops on the shared list only.</p>
          <table className="w-full text-sm">
            <thead className="text-xs text-ink/70 border-b border-line">
              <tr>
                <th className="text-left font-normal py-2 pr-3">Category</th>
                <th className="text-right font-normal py-2 px-2">Average</th>
                <th className="text-right font-normal py-2 pl-2">Most people</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {averages.categories.map((c) => (
                <tr key={c.subject}>
                  <td className="py-2 pr-3 text-ink">{c.name}</td>
                  <td className="py-2 px-2 text-right font-mono font-tabular">{pounds(c.mean)}</td>
                  <td className="py-2 pl-2 text-right font-mono font-tabular text-xs text-ink/70">
                    {pounds(c.p25)}–{pounds(c.p75)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  )
}

function ShopRow({
  figure,
  own,
  sharing,
}: {
  figure: SpendFigure
  own: { visits: number; amount: number } | undefined
  sharing: boolean
}) {
  const ownPence = own ? Math.round(own.amount * 100) : null
  const result = ownPence === null ? null : compare(ownPence, figure)
  return (
    <tr>
      <td className="py-2 pr-3 text-ink">
        {figure.name}
        {figure.visits > 0 && (
          <span className="block text-xs text-ink/70">
            {figure.visits.toFixed(1)} visits, {pounds(Math.round(figure.mean / figure.visits))} a visit
          </span>
        )}
      </td>
      <td className="py-2 px-2 text-right font-mono font-tabular">{pounds(figure.mean)}</td>
      <td className="py-2 px-2 text-right font-mono font-tabular text-xs text-ink/70">
        {pounds(figure.p25)}–{pounds(figure.p75)}
      </td>
      {sharing && (
        <td className="py-2 px-2 text-right font-mono font-tabular">{ownPence === null ? '—' : pounds(ownPence)}</td>
      )}
      {sharing && (
        <td className="py-2 pl-2 text-xs">
          {result ? (
            <>
              <span className={result.band === 'typical' ? 'text-ink' : result.band === 'above' ? 'text-rag-bad' : 'text-accent'}>
                {differenceText(result.difference)}
              </span>
              <span className="block text-ink/70">{BAND_TEXT[result.band]}</span>
            </>
          ) : (
            <span className="text-ink/70">Not imported</span>
          )}
        </td>
      )}
    </tr>
  )
}
